import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Eye, X } from "lucide-react";
import { isPreviewModeEnabled, exitPreviewMode } from "../config/serviceLines";

/**
 * Amber banner shown at the top of every page whenever the current URL has
 * ?preview=true. Purely URL-driven — no localStorage / sessionStorage
 * persistence — so it never leaks to admin sessions or real customers.
 */
export default function PreviewModeBanner() {
  const location = useLocation();
  const [previewActive, setPreviewActive] = useState(false);

  useEffect(() => {
    // Defensive one-time cleanup for older builds that wrote to storage.
    try {
      if (typeof window !== "undefined") {
        localStorage.removeItem("preview_mode");
        sessionStorage.removeItem("preview_mode");
      }
    } catch { /* storage unavailable — nothing to clean */ }
    setPreviewActive(isPreviewModeEnabled());
  }, [location.pathname, location.search]);

  if (!previewActive) return null;

  const handleExit = () => {
    exitPreviewMode();
    const url = new URL(window.location.href);
    url.searchParams.delete("preview");
    window.location.replace(url.pathname + (url.search ? url.search : "") + url.hash);
  };

  return (
    <div
      className="w-full bg-gradient-to-r from-amber-500/95 to-orange-500/95 text-black text-center py-2 px-4 flex items-center justify-center gap-3 relative z-50 shadow-lg"
      data-testid="preview-mode-banner"
    >
      <Eye className="w-4 h-4 shrink-0" />
      <span className="text-sm font-medium">
        <strong className="font-bold">Preview mode</strong> — this URL includes <code className="text-xs">?preview=true</code> so hidden services are visible. Remove the flag or navigate away to return to the customer view.
      </span>
      <button
        onClick={handleExit}
        className="ml-2 inline-flex items-center gap-1 bg-black/20 hover:bg-black/30 rounded-full px-2 py-0.5 text-xs font-medium transition-colors"
        data-testid="preview-mode-exit"
      >
        Exit <X className="w-3 h-3" />
      </button>
    </div>
  );
}
